import { CancelOutlined } from '@mui/icons-material';
import { Box, Dialog, IconButton, TextField, Tooltip } from '@mui/material';
import color from '@theme/Colors';
import { UsePopUpReturnType } from '@hooks/usePopUp';
import { ChangeEvent, Dispatch, KeyboardEvent, SetStateAction, useState } from 'react';
import { AttachmentsFiles, blobURLCameraAttachmentsPhoto } from '../config';
import { appConfig } from '@configs/index';
import DeleteImageIcon from '@assets/jsx-icon/DeleteImageIcon';

export type AttachmentFileItemProps = {
    fileItem: AttachmentsFiles;
    index: number;
    isEditing: boolean;
    conditionLengthText: boolean;
    attachmentsFileItem: AttachmentsFiles;
    attachmentsDescription?: string;
    popupConfirmDeleteAttachmentsFile: UsePopUpReturnType;
    handleKeyboardEvent: (e: KeyboardEvent<HTMLDivElement>, fileId: number) => void;
    handleBlurTextField: (fileId: number) => void;
    handleTypingChangeDescription: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDeleteAttachmentsFile: (indexFile: string) => void;
    onSetAttachmentsFileItem: (fileItem: AttachmentsFiles) => void;
    handleClickUpsertDescription: (indexFile: AttachmentsFiles) => void;
};

export default function AttachmentFileItem(props: AttachmentFileItemProps) {
    const {
        fileItem,
        index,
        conditionLengthText,
        handleDeleteAttachmentsFile,
        popupConfirmDeleteAttachmentsFile,
        onSetAttachmentsFileItem,
        handleClickUpsertDescription,
        attachmentsFileItem,
        isEditing,
        attachmentsDescription,
        handleKeyboardEvent,
        handleBlurTextField,
        handleTypingChangeDescription,
    } = props;

    const [selectedImgPreview, setSelectedImgPreview] = useState<string | null>(null);
    const [openImgPreview, setOpenImgPreview] = useState<boolean>(false);

    const fileNameBlob = fileItem.BlobName
        ? `${blobURLCameraAttachmentsPhoto}${fileItem.BlobName}`
        : URL.createObjectURL(fileItem.fileUrl!);

    return (
        <Box
            key={fileItem._fakeId}
            position="relative"
            flexBasis="20%"
            maxWidth="90px"
            pb={1}
            ml={2}
            mt={index >= 6 ? 4 : 0}
        >
            <DeleteImageIcon
                style={{
                    position: 'absolute',
                    top: -7,
                    right: -7,
                    zIndex: 1,
                    fill: color.textSecondary,
                    backgroundColor: color.white,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    padding: '1px',
                    opacity: 0.9,
                }}
                onClick={() => {
                    if (fileItem.Id) {
                        popupConfirmDeleteAttachmentsFile.setTrue();
                        onSetAttachmentsFileItem(fileItem);
                    } else {
                        handleDeleteAttachmentsFile(fileItem._fakeId!);
                    }
                }}
            />

            <Tooltip title={fileItem.Desc ? fileItem.Desc : ''} placement="top">
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-40px',
                        left: conditionLengthText ? '10%' : '5%',
                        maxWidth: '90px',
                        transform: 'translateX(-50%)',
                        background: '#505050',
                        color: '#fff',
                        padding: '4px 8px',
                        borderRadius: '16px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        fontSize: '12px',
                        textAlign: 'center',
                        opacity: 0.9,
                        zIndex: 2,
                        '&::before': conditionLengthText
                            ? {
                                  content: '""',
                                  position: 'absolute',
                                  width: '12px',
                                  height: '10px',
                                  backgroundColor: '#505050',
                                  borderRadius: '50%',
                                  left: '30%',
                                  top: 'calc(100% - 5px)',
                                  transform: 'translateX(-50%)',
                                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                              }
                            : {},
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#505050',
                            borderRadius: '9999px',
                            left: '40%',
                            top: 'calc(100% + 10px)',
                            transform: 'translateX(-50%)',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        },
                    }}
                    onClick={() => {
                        handleClickUpsertDescription(fileItem);
                    }}
                >
                    {isEditing &&
                    ((attachmentsFileItem.Id && attachmentsFileItem.Id === fileItem.Id) ||
                        (!attachmentsFileItem.Id && attachmentsFileItem._fakeId === fileItem._fakeId)) ? (
                        <TextField
                            autoFocus
                            size="small"
                            multiline
                            maxRows={2}
                            sx={{
                                background: 'transparent',
                                width: '90px',
                                wordBreak: 'break-word',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                    outline: 'none',
                                },
                                '& .MuiInputBase-root': {
                                    padding: '0 5px 0 0',
                                    height:
                                        attachmentsDescription?.length! < 8 || !attachmentsDescription
                                            ? '20px'
                                            : 'auto',
                                },
                                '& .MuiInputBase-input': {
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    overflow: 'hidden',
                                    padding: '0 5px 0 0',
                                    color: color.white,
                                    fontSize:
                                        attachmentsDescription?.length! < 14 || !attachmentsDescription
                                            ? '12px'
                                            : '11px',
                                },
                            }}
                            value={attachmentsDescription}
                            onKeyDown={(e) => {
                                handleKeyboardEvent(e, fileItem.Id!);
                            }}
                            onBlur={() => {
                                handleBlurTextField(fileItem.Id!);
                            }}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                if (newValue.length <= 255) {
                                    handleTypingChangeDescription(e as ChangeEvent<HTMLInputElement>);
                                }
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                            }}
                        >
                            {fileItem.Desc ? fileItem.Desc : 'Add desc'}
                        </Box>
                    )}
                </Box>
            </Tooltip>

            <img
                src={fileNameBlob}
                alt="AttachmentsPhoto"
                width="90px"
                height="90px"
                style={{
                    borderRadius: '4px',
                    cursor: 'pointer',
                    objectFit: 'cover',
                }}
                onClick={() => {
                    setSelectedImgPreview(fileNameBlob);
                    setOpenImgPreview(true);
                }}
            />

            {selectedImgPreview && (
                <Dialog
                    open={!!openImgPreview}
                    onClose={() => {
                        setOpenImgPreview(false);
                        setSelectedImgPreview(null);
                    }}
                    PaperProps={{
                        sx: {
                            minWidth: 1280,
                            minHeight: 720,
                        },
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 1280,
                            minHeight: 720,
                            background: `url(${selectedImgPreview}) no-repeat center center`,
                            backgroundSize: 'cover',
                            borderRadius: '4px',
                            objectFit: 'contain',
                        }}
                    />
                </Dialog>
            )}
        </Box>
    );
}
